// Vendors
import React, { useEffect } from 'react'
import { Controller, FieldError, useFormContext } from 'react-hook-form'

// Functions
import { useSelectParser } from 'hooks/useSelectParser'

// Components
import { FieldText } from 'components/FieldText'
import { FieldNumber } from 'components/FieldNumber'
import { FieldSelect } from 'components/FieldSelect'
import { FieldTextArea } from 'components/FieldTextArea'

// Styles
import { Grid, GridItem } from '@chakra-ui/react'

// Types
import { LaboratorialAnalysis } from 'types/LaboratorialAnalysis'
import { useFormTransform } from 'hooks/useFormTransform'

export const TabGeneral = () => {
  /*
  |-----------------------------------------------------------------------------
  | Options
  |-----------------------------------------------------------------------------
  |
  |
  */

  const selectLaboratorialAnalysis = useSelectParser<LaboratorialAnalysis>({
    accessor: { value: 'id', label: 'name' },
    endpoint: '/app/laboratory_analysis',
  })

  /*
  |-----------------------------------------------------------------------------
  | Constants
  |-----------------------------------------------------------------------------
  |
  |
  */

  const {
    control,
    register,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext()

  const { selectParser } = useFormTransform()
  const laboratoryAnalysisIds = getValues('laboratoryAnalysisIds')

  /*
  |-----------------------------------------------------------------------------
  | Effects
  |-----------------------------------------------------------------------------
  |
  |
  */

  useEffect(() => {
    if (!laboratoryAnalysisIds) return
    if (selectLaboratorialAnalysis.options.length === 0) return
    if (typeof laboratoryAnalysisIds[0] === 'object') return

    const laboratoryAnalysisIdsOption = selectParser(
      laboratoryAnalysisIds,
      selectLaboratorialAnalysis.options
    )

    setValue('laboratoryAnalysisIds', laboratoryAnalysisIdsOption)
  }, [
    setValue,
    selectParser,
    laboratoryAnalysisIds,
    selectLaboratorialAnalysis,
  ])

  /*
  |-----------------------------------------------------------------------------
  | Renders
  |-----------------------------------------------------------------------------
  |
  |
  */

  return (
    <Grid
      gap="10"
      my="4"
      templateColumns={{ lg: 'repeat(4, 1fr)', xl: 'repeat(8, 1fr)' }}
    >
      <GridItem colSpan={4}>
        <FieldText
          label="Nome"
          error={errors.name}
          {...register('name')}
          isRequired
        />
      </GridItem>

      <GridItem>
        <FieldNumber
          min={1}
          max={999}
          control={control}
          name="heightInCentimeters"
          label="Altura (cm)"
          error={errors.heightInCentimeters}
          isRequired
        />
      </GridItem>

      <GridItem>
        <FieldNumber
          min={1}
          max={999}
          control={control}
          name="widthInCentimeters"
          label="Largura (cm)"
          error={errors.widthInCentimeters}
          isRequired
        />
      </GridItem>

      <GridItem>
        <FieldNumber
          min={1}
          max={999}
          control={control}
          name="lengthInCentimeters"
          label="Comprimento (cm)"
          error={errors.lengthInCentimeters}
          isRequired
        />
      </GridItem>

      <GridItem>
        <FieldNumber
          min={1}
          max={999}
          control={control}
          name="weightInGrams"
          label="Peso (g)"
          error={errors.weightInGrams}
        />
      </GridItem>

      <GridItem colSpan={4}>
        <FieldTextArea
          label="Descrição"
          error={errors.description}
          isRequired
          {...register('description')}
        />
      </GridItem>

      <GridItem colSpan={4}>
        <Controller
          name="laboratoryAnalysisIds"
          control={control}
          render={({ field }) => (
            <FieldSelect
              label="Selecione as análises laboratoriais"
              isMulti
              error={errors.laboratoryAnalysisIds}
              options={selectLaboratorialAnalysis.options}
              isLoading={selectLaboratorialAnalysis.isLoading}
              isRequired
              {...field}
            />
          )}
        />
      </GridItem>

      <GridItem colSpan={2}>
        <FieldText
          label="SKU"
          error={errors.sku}
          {...register('sku')}
          isRequired
        />
      </GridItem>
    </Grid>
  )
}
